import { Controller, useForm } from "react-hook-form";
import { gsap } from "gsap";
import { useEffect, useRef, useState } from "react";
import BigNumber from "bignumber.js";
import { useConnection } from "@solana/wallet-adapter-react";
import { PublicKey } from "@solana/web3.js";
import Button from "@axvdex/components/common/Button";
import { Bidder, SaleBidAsset, OutbidSale, Sale } from "@axvdex/types/solana/outbid";
import { placeBid, BidArgs } from "@axvdex/hooks/solana/useOutbid";
import { ReactComponent as IcnTwitter } from "@axvdex/assets/icons/icn-twitterx.svg";
import useLanguage from "@axvdex/hooks/useLanguage";
import { useAppDispatch, useAppSelector } from "@axvdex/state";
import { selectWalletInfo } from "@axvdex/state/wallet/walletSelectors";
import CustomSelect, { ListItemsProps } from "@axvdex/components/form-element/CustomSelect";
import CustomNumericInput from "@axvdex/components/form-element/CustomNumericInput";
import UserBalance from "@axvdex/components/user/UserBalance";
import imgSanitize from "@axvdex/utils/imgSanitize";
import { q6464ToF64 } from "@axvdex/hooks/solana/utils/convertion";
import { sendToast } from "@axvdex/state/wallet/walletSlice";
import { useUserBalanceContext } from "@axvdex/hooks/solana/useUserBalance";
import SaleIncentiveCongratsModal from "./SolanaSaleIncentiveCongratsModal";

type SolanaSaleBidSectionProps = {
  outbidSale: OutbidSale;
  selectedSale: Sale;
  bidderInfo: Bidder | null;
  onWhitelist: boolean;
  setOnWhitelist: (open: boolean) => void;
};

const SolanaSaleBidSection = ({
  outbidSale,
  selectedSale,
  bidderInfo,
  onWhitelist,
  setOnWhitelist,
}: SolanaSaleBidSectionProps) => {
  const dispatch = useAppDispatch();
  const { i18 } = useLanguage();
  const { control } = useForm();
  const walletInfo = useAppSelector(selectWalletInfo);
  const [selectedBidAsset, setSelectedBidAsset] = useState<ListItemsProps | null>(null);
  const [bidInputAmount, setBidInputAmount] = useState<string>("");
  const [minBidAmount, setMinBidAmount] = useState<string>(null);
  const [isTransactionLoading, setIsTransactionLoading] = useState(false);
  // 1/2 comment to open by default the congrats model for testing
  const [saleIncentiveCongratsModalOpen, setSaleIncentiveCongratsModalOpen] = useState({ open: false, incentives: [] });
  // 2/2 uncomment to open by default the congrats model for testing,BidSec
  // IGNORE ERRORS if browser routes to deeplink, and use parent URL to avoid errors
  // const [saleIncentiveCongratsModalOpen, setSaleIncentiveCongratsModalOpen] = useState({
  //   open: true,
  //   incentives: [
  //     {
  //       // An NFT on Devnet
  //       mint: new PublicKey("8dP75EWHnYAc6pgeiToXtE66qEaHx1PcoetZpmnAQeAp"),
  //       amount: new BigNumber("1"),
  //     },
  //   ],
  // });
  const { connection } = useConnection();
  const { assetBalances: userAssetBalances, triggerFetchUserAssetBalances, isBalanceLoading } = useUserBalanceContext();

  useEffect(() => {
    triggerFetchUserAssetBalances();
  }, [bidderInfo, walletInfo]);

  // keep track of minBidAmount, so it can update the state when it receives the websocket update
  useEffect(() => {
    if (selectedSale && minBidAmount !== selectedSale.config.saleSettings.minTicket.toString(10)) {
      const minTicketsHumanFormat = BigNumber(minBidAmount)
        .div(Math.pow(10, selectedSale.config.targetAllocation.targetAssetDecimals.toNumber()))
        .decimalPlaces(0)
        .toString(10);
      if (minTicketsHumanFormat === bidInputAmount) {
        setBidInputAmount(
          BigNumber(selectedSale.config.saleSettings.minTicket)
            .div(Math.pow(10, selectedSale.config.targetAllocation.targetAssetDecimals.toNumber()))
            .decimalPlaces(0)
            .toString(10)
        );
      }
      setMinBidAmount(selectedSale.config.saleSettings.minTicket.toString(10));
    }

    const remainingTickets = getRemainvingTicketAmount(selectedSale, false);
    if (BigNumber(selectedSale.config.saleSettings.minTicket).gt(remainingTickets)) {
      setBidInputAmount(
        BigNumber(remainingTickets)
          .div(Math.pow(10, selectedSale.config.targetAllocation.targetAssetDecimals.toNumber()))
          .decimalPlaces(0)
          .toString(10)
      );
    }
  }, [selectedSale]);

  // Init the balance of the selected
  useEffect(() => {
    if (outbidSale.assets && selectedSale && !selectedBidAsset && minBidAmount) {
      const bidAsset = selectedSale.config.bidAssets[0];
      const mint = bidAsset.mint.toBase58();
      const symbol = outbidSale.assets[mint].symbol;
      if (outbidSale.assets[mint])
        setSelectedBidAsset({
          value: symbol,
          label: symbol,
          id: mint,
          optionPrefix: <img src={imgSanitize(symbol)} alt={""} />,
        });
      setBidInputAmount(
        BigNumber(minBidAmount)
          .div(Math.pow(10, selectedSale.config.targetAllocation.targetAssetDecimals.toNumber()))
          .decimalPlaces(0)
          .toString(10)
      );
    }
    // initialize get balances when everything is ready
    if (walletInfo && walletInfo.isConnected && selectedSale && Object.keys(userAssetBalances).length <= 1)
      triggerFetchUserAssetBalances();
  }, [selectedSale, minBidAmount, walletInfo]);

  const convertAssetAmountToTicketNumber = (assetId: string, inputAmount: string) => {
    const selectedAsset = selectedSale.config.bidAssets.find(asset => asset.mint.toBase58() === assetId);
    const ratio = q6464ToF64(selectedAsset.assetToTicketRatio);
    return BigNumber(inputAmount).times(ratio).decimalPlaces(4, BigNumber.ROUND_FLOOR).toString(10);
  };

  const convertTicketNumberToAssetAmount = (assetId: string, ticketAmount: string) => {
    const selectedAsset = selectedSale.config.bidAssets.find(asset => asset.mint.toBase58() === assetId);
    const ratio = q6464ToF64(selectedAsset.assetToTicketRatio);
    const res = BigNumber(ticketAmount)
      .div(ratio)
      .decimalPlaces(
        6,
        ticketAmount ===
          BigNumber(minBidAmount)
            .div(Math.pow(10, selectedSale.config.targetAllocation.targetAssetDecimals.toNumber()))
            .decimalPlaces(0)
            .toString(10)
          ? BigNumber.ROUND_CEIL
          : BigNumber.ROUND_FLOOR
      )
      .toString(10);
    return res;
  };

  const buttonConfig = () => {
    const disabledConfig = { disabled: true, extraClass: "" };
    const action = i18("Bid", "sale.bidSection.bid");

    if (!walletInfo.isConnected) {
      return {
        buttonText: i18("Connect wallet", "trade.submitBtn.text.connectWallet"),
        ...disabledConfig,
      };
    }

    if ("WaitingToStart" === selectedSale.status) {
      return {
        buttonText: i18("Sale didn't start yet", "sale.bidSection.saleNotStarted"),
        ...disabledConfig,
      };
    }

    if (["EndedSoldOut", "EndedNotSoldOut", "EndedBonusTimeSoldOut"].includes(selectedSale.status)) {
      return {
        buttonText: i18("Sale ended", "sale.bidSection.saleEnded"),
        ...disabledConfig,
      };
    }

    if ("WhitelistBidding" === selectedSale.status && !onWhitelist) {
      return {
        buttonText: i18("VIP bid only", "sale.bidSection.vipBidOnly"),
        ...disabledConfig,
      };
    }

    if (!bidInputAmount) {
      return {
        buttonText: i18("Enter Amount", "sale.bidSection.enterAmount"),
        ...disabledConfig,
      };
    }

    if (selectedSale.config.isCancelled) {
      return {
        buttonText: i18("Sale Canceled", "sale.bidSection.enterAmount"),
        ...disabledConfig,
      };
    }

    if (selectedSale.config.isStopped) {
      return {
        buttonText: i18("Sale Stopped", "sale.bidSection.enterAmount"),
        ...disabledConfig,
      };
    }

    if (
      BigNumber(bidInputAmount.replaceAll(",", "")).gt(
        BigNumber(selectedSale.config.saleSettings.maxTicket).div(
          Math.pow(10, selectedSale.config.targetAllocation.targetAssetDecimals.toNumber())
        )
      )
    ) {
      return {
        buttonText: i18(
          `Max ${BigNumber(selectedSale.config.saleSettings.maxTicket).div(
            Math.pow(10, selectedSale.config.targetAllocation.targetAssetDecimals.toNumber())
          )} tickets per TX`,
          "sale.bidSection.maxTickets",
          {
            amount: BigNumber(selectedSale.config.saleSettings.maxTicket).div(
              Math.pow(10, selectedSale.config.targetAllocation.targetAssetDecimals.toNumber())
            ),
          }
        ),
        ...disabledConfig,
      };
    }

    const remainingTickets = getRemainvingTicketAmount(selectedSale);
    if (
      !remainingTickets.lt(
        BigNumber(minBidAmount).div(Math.pow(10, selectedSale.config.targetAllocation.targetAssetDecimals.toNumber()))
      ) &&
      BigNumber(bidInputAmount.replaceAll(",", "")).lt(
        BigNumber(minBidAmount).div(Math.pow(10, selectedSale.config.targetAllocation.targetAssetDecimals.toNumber()))
      )
    ) {
      return {
        buttonText: i18(
          `Min ${BigNumber(minBidAmount).div(
            Math.pow(10, selectedSale.config.targetAllocation.targetAssetDecimals.toNumber())
          )} tickets per TX`,
          "sale.bidSection.minTicketsBtn",
          {
            amount: BigNumber(minBidAmount).div(
              Math.pow(10, selectedSale.config.targetAllocation.targetAssetDecimals.toNumber())
            ),
          }
        ),
        ...disabledConfig,
      };
    }

    if (
      remainingTickets.lt(
        BigNumber(minBidAmount).div(Math.pow(10, selectedSale.config.targetAllocation.targetAssetDecimals.toNumber()))
      ) &&
      remainingTickets.toString(10) !== bidInputAmount.replaceAll(",", "")
    ) {
      return {
        buttonText: i18(`Only ${remainingTickets.toString(10)} tickets per TX`, "sale.bidSection.onlyTicketsBtn", {
          amount: remainingTickets.toString(10),
        }),
        ...disabledConfig,
      };
    }

    // Check if there is a wallet cap | Not in bonus bidding
    if (
      bidderInfo &&
      selectedSale &&
      "BonusBidding" !== selectedSale.status &&
      selectedSale.config.saleSettings.walletMaxTicketCap &&
      BigNumber(bidInputAmount.replaceAll(",", ""))
        .plus(
          BigNumber(bidderInfo.boughtTicketsAmount.toString(10)).div(
            Math.pow(10, selectedSale.config.targetAllocation.targetAssetDecimals.toNumber())
          )
        )
        .gt(
          BigNumber(selectedSale.config.saleSettings.walletMaxTicketCap).div(
            Math.pow(10, selectedSale.config.targetAllocation.targetAssetDecimals.toNumber())
          )
        )
    ) {
      return {
        buttonText: i18(
          `Wallet can only buy a total of ${BigNumber(selectedSale.config.saleSettings.walletMaxTicketCap)
            .div(Math.pow(10, selectedSale.config.targetAllocation.targetAssetDecimals.toNumber()))
            .toString(10)} tickets`,
          "sale.bidSection.walletCapBtn",
          {
            amount: BigNumber(selectedSale.config.saleSettings.walletMaxTicketCap)
              .div(Math.pow(10, selectedSale.config.targetAllocation.targetAssetDecimals.toNumber()))
              .toString(10),
          }
        ),
        ...disabledConfig,
      };
    }

    // Question ? Shoud we check if there is a wallet cap in bonus bidding

    if (isBalanceLoading) {
      return {
        buttonText: i18("Loading...", "trade.submitBtn.text.loading"),
        ...disabledConfig,
      };
    }

    if (isTransactionLoading) {
      return {
        buttonText: i18("Executing...", "trade.submitBtn.text.executing"),
        ...disabledConfig,
      };
    }

    if (
      selectedBidAsset &&
      userAssetBalances[selectedBidAsset.id] &&
      BigNumber(
        convertTicketNumberToAssetAmount(
          selectedBidAsset.id,
          "" !== bidInputAmount ? bidInputAmount.replaceAll(",", "") : "0"
        )
      ).gt(BigNumber(userAssetBalances[selectedBidAsset.id]))
    ) {
      return {
        buttonText: i18(`Not enough balance`, "sale.bidSection.notEnoughBalance"),
        ...disabledConfig,
      };
    }

    return {
      buttonText: action,
      extraClass: "",
      disabled: false,
    };
  };

  const handleUserBid = async () => {
    setIsTransactionLoading(true);
    try {
      // Convert the amount from ticket to asset
      const ticketAmount = "" !== bidInputAmount ? bidInputAmount.replaceAll(",", "") : "0";
      const bidAssetAmount = BigNumber(convertTicketNumberToAssetAmount(selectedBidAsset.id, ticketAmount))
        .times(Math.pow(10, outbidSale.assets[selectedBidAsset.id].decimals))
        .decimalPlaces(0, BigNumber.ROUND_UP)
        .toNumber();

      // Error if asset amount is 0
      if (bidAssetAmount <= 0) {
        throw new ErrorToDisplay("Please enter a number of tickets greater than 0");
      }

      // Retreive the asset id from the sale and the selectedBidAsset
      const bidAssetIndex = selectedSale.config.bidAssets.findIndex(
        asset => asset.mint.toBase58() === selectedBidAsset.id
      );

      // Error if the user don't have enough SOL to pay the fees
      const solBalance = await connection.getBalance(new PublicKey(walletInfo.walletAddress));
      console.log("solBalance", solBalance);
      if (BigNumber(solBalance).eq(0)) {
        throw new ErrorToDisplay("Not enough SOL to pay the transaction fee");
      }

      // Build the args for the bid
      const args: BidArgs = {
        bidAssetIndex,
        bidAssetAmount: bidAssetAmount,
      };

      // Execute the bid
      let incentivesWon = [];
      try {
        const nbTicket = BigNumber(ticketAmount).times(
          Math.pow(10, selectedSale.config.targetAllocation.targetAssetDecimals.toNumber())
        );
        incentivesWon = await placeBid(connection, selectedSale, args, nbTicket);
      } catch (err) {
        if ("User rejected the request." === err.message) {
          throw err;
        } else {
          throw new ErrorToDisplay("Not able to place bid. please try again.");
        }
      }

      // Check if the user won any incentives
      if (incentivesWon.length > 0) {
        setSaleIncentiveCongratsModalOpen({
          open: true,
          incentives: incentivesWon,
        });
      }

      // if the user bidded, he is on the whitelist
      setOnWhitelist(true);
    } catch (err) {
      if (err instanceof ErrorToDisplay) {
        const msg = err.message;
        dispatch(sendToast({ type: "tx-fail", info: { msg, toastID: "" + new Date().getTime() } }));
      } else {
        console.error("Not able to place bid.", err);
      }
      return;
    } finally {
      setIsTransactionLoading(false);
    }
  };

  return (
    <div style={{ marginBottom: "1em" }}>
      <div style={{ display: "flex" }}>
        <h3>
          {i18(`Buy tickets for ${outbidSale.projectSymbol} token`, "sale.bid.section", {
            project: outbidSale.projectSymbol,
          })}
        </h3>
      </div>

      <div className="input-bid-form-wrapper" style={{ marginBottom: "1em" }}>
        <form>
          <fieldset className="tradeFromTokenFieldset withGradientBorder">
            <fieldset className="selectTokenSubFieldset" style={{ paddingBottom: ".5em" }}>
              <div style={{ display: "flex", textAlignLast: "right" }}>
                <Button
                  style={{ marginRight: ".5em" }}
                  btnColor="dark-medium"
                  text={i18("Max", "sale.bidSection.maxBtn")}
                  title={i18("Select max value", "sale.bidSection.maxBtnSelect")}
                  onClick={() => {
                    const maxTicketsThatUserCanBuy = convertAssetAmountToTicketNumber(
                      selectedBidAsset.id,
                      BigNumber(userAssetBalances[selectedBidAsset.id]).toString(10)
                    );
                    const remainingTickets = getRemainvingTicketAmount(selectedSale);

                    let amountToInput = null;
                    if (
                      BigNumber(maxTicketsThatUserCanBuy).lt(
                        BigNumber(selectedSale.config.saleSettings.maxTicket).div(
                          Math.pow(10, selectedSale.config.targetAllocation.targetAssetDecimals.toNumber())
                        )
                      )
                    ) {
                      amountToInput = maxTicketsThatUserCanBuy;
                    } else {
                      amountToInput = BigNumber(selectedSale.config.saleSettings.maxTicket)
                        .div(Math.pow(10, selectedSale.config.targetAllocation.targetAssetDecimals.toNumber()))
                        .toString(10);
                    }

                    if (BigNumber(BigNumber(amountToInput).toString(10)).gt(remainingTickets)) {
                      setBidInputAmount(
                        BigNumber(remainingTickets.decimalPlaces(6, BigNumber.ROUND_DOWN).toString(10)).toString(10)
                      );
                    } else {
                      setBidInputAmount(amountToInput);
                    }
                  }}
                />
                <Button
                  style={{ marginRight: ".5em" }}
                  btnColor="dark-medium"
                  text={i18("Min", "sale.bidSection.minBtn")}
                  title={i18("Select min value", "sale.bidSection.minBtnSelect")}
                  onClick={() => {
                    const remainingTickets = getRemainvingTicketAmount(selectedSale, false);
                    const minTickets = selectedSale.config.saleSettings.minTicket;
                    setBidInputAmount(
                      BigNumber(BigNumber(remainingTickets).lt(minTickets) ? remainingTickets : minTickets)
                        .div(Math.pow(10, selectedSale.config.targetAllocation.targetAssetDecimals.toNumber()))
                        .decimalPlaces(6)
                        .toString(10)
                    );
                  }}
                />
                <CustomNumericInput
                  extraClassName="fillAvailable"
                  name="bidInput"
                  labelText={i18("Choose amount", "trade.form.fromToken.input.label")}
                  value={bidInputAmount}
                  onChange={e => setBidInputAmount(e.target.value)}
                  hiddenLabel={true}
                  placeholder={"0"}
                  decimalScale={6}
                />
              </div>

              <MinTicketsComponent
                minTickets={selectedSale.config.saleSettings.minTicket.toNumber()}
                remainingTickets={getRemainvingTicketAmount(selectedSale, false).toString(10)}
                outbidSale={outbidSale}
                selectedSale={selectedSale}
              />
            </fieldset>

            <fieldset
              className="selectTokenSubFieldset tradeFromToken"
              style={{ borderBottom: "none", paddingBottom: "0em" }}
            >
              <div className="flexbox">
                <Controller
                  name="trade_from_token"
                  control={control}
                  rules={{ required: true }}
                  // eslint-disable-next-line @typescript-eslint/no-unused-vars
                  render={({ field: { onChange, ...field } }) => {
                    return (
                      <CustomSelect
                        {...field}
                        labelText={""}
                        hiddenLabel={true}
                        items={selectedSale.config.bidAssets
                          .map((asset: SaleBidAsset) => {
                            const mint = asset.mint.toBase58();
                            const symbol = outbidSale.assets[mint].symbol;
                            if (outbidSale.assets[mint]) {
                              return {
                                value: symbol,
                                label: symbol,
                                id: mint,
                                optionPrefix: <img src={imgSanitize(symbol)} alt={""} />,
                              };
                            }
                            return null;
                          })
                          .filter(asset => asset)}
                        placeholder={i18("Select token", "trade.form.fromToken.select.placeholder")}
                        value={selectedBidAsset}
                        onChange={(val: ListItemsProps) => {
                          setSelectedBidAsset(val);
                          setBidInputAmount(
                            BigNumber(minBidAmount)
                              .div(Math.pow(10, selectedSale.config.targetAllocation.targetAssetDecimals.toNumber()))
                              .decimalPlaces(0)
                              .toString(10)
                          );
                        }}
                      />
                    );
                  }}
                />

                <CustomNumericInput
                  extraClassName="gradientText"
                  name=""
                  labelText={""}
                  hiddenLabel={true}
                  placeholder={"0"}
                  value={
                    selectedBidAsset
                      ? convertTicketNumberToAssetAmount(
                        selectedBidAsset.id,
                        "" !== bidInputAmount ? bidInputAmount.replaceAll(",", "") : "0"
                      )
                      : "0"
                  }
                  decimalScale={6}
                  disabled={true}
                />
              </div>
            </fieldset>
            {selectedBidAsset && (
              <div className="selectTokenBalanceAmount" style={{ justifyContent: "right" }}>
                <UserBalance
                  balanceValue={BigNumber(userAssetBalances[selectedBidAsset.id]).toNumber()}
                  balanceUsdValue={
                    BigNumber(userAssetBalances[selectedBidAsset.id]).toNumber() *
                    outbidSale.assets[selectedBidAsset.id].price || 0
                  }
                />
              </div>
            )}
            <div style={{ display: "flex" }}>
              <Button
                title={i18("Share on X", "sale.share.x")}
                style={{ padding: "1em", margin: ".5em", marginTop: "1em" }}
                btnColor="dark-medium"
                onClick={() => {
                  const remainingTickets = getRemainvingTicketAmount(selectedSale);
                  const valueRemaining = remainingTickets.decimalPlaces(0).toNumber().toLocaleString("en-US");

                  const winString =
                    "0" === valueRemaining
                      ? `Join in now for a chance to win ${outbidSale.projectSymbol} tokens%21`
                      : `Join in now for a chance to win %7E%24${valueRemaining} in ${outbidSale.projectSymbol} tokens%21`;

                  window.open(
                    "https://twitter.com/intent/tweet?text=" +
                    i18(
                      `I'm becoming my own VC by participating on Outbid%2C the gamified %23DeFi launchpad for the %23Solana built on %40axvdex%2C where every bid is a winner%21 ` +
                      winString,
                      "sale.share.x.post",
                      { prize: winString, project: outbidSale.name }
                    ).replace(/(%20|\s)+/g, "+")
                  );
                }}
              >
                <IcnTwitter style={{ width: "1em", height: "1em" }} />
              </Button>
              <Button
                style={{ margin: ".5em", marginRight: "0em", marginTop: "1em" }}
                btnColor="gradient"
                isFullWidth={true}
                text={buttonConfig().buttonText}
                title={i18("Submit", "trade.submitBtn.title")}
                disabled={buttonConfig().disabled}
                extraClassName={buttonConfig().extraClass}
                onClick={() => handleUserBid()}
              />
            </div>
          </fieldset>
        </form>
      </div>
      <SaleIncentiveCongratsModal
        saleIncentiveCongratsModalOpen={saleIncentiveCongratsModalOpen}
        setSaleIncentiveCongratsModalOpen={setSaleIncentiveCongratsModalOpen}
        outbidSale={outbidSale}
      />
    </div>
  );
};

export default SolanaSaleBidSection;

type MinTicketsComponentProps = {
  outbidSale: OutbidSale;
  selectedSale: Sale;
  minTickets: number;
  remainingTickets: string;
};

const MinTicketsComponent = ({ minTickets, remainingTickets, outbidSale, selectedSale }: MinTicketsComponentProps) => {
  const { i18 } = useLanguage();
  const app = useRef<HTMLDivElement>(null);
  const prevAmount = useRef({ minTickets }).current;

  useEffect(() => {
    if (minTickets !== prevAmount.minTickets) {
      const ctx = gsap.context(() => {
        gsap.from(`.minTickets`, 1, {
          ease: "Power3.easeInOut",
          duration: 0.5,
          y: "50%",
          opacity: 0,
        });
      }, app);
      return () => ctx.revert();
    }
  }, [minTickets]);

  return (
    <div style={{ display: "flex", marginTop: ".5em", marginBottom: "0em" }}>
      <div style={{ marginLeft: "auto", marginRight: "1.5em" }}>
        <small style={{ fontSize: ".65em", color: "var(--warm-grey)" }}>
          1 Ticket ={" "}
          {BigNumber(1)
            .div(
              BigNumber(selectedSale.config.targetAllocation.targetTicketAmount).div(
                selectedSale.config.targetAllocation.targetAssetAllocationAmount
              )
            )
            .decimalPlaces(2)
            .toString(10)}{" "}
          {outbidSale.projectSymbol}
        </small>
      </div>

      <div ref={app}>
        <small style={{ fontSize: ".65em", color: "var(--warm-grey)" }}>
          {i18("Minimum tickets", "sale.bidSection.minTickets")}:{" "}
          {BigNumber(BigNumber(remainingTickets).lt(minTickets) ? remainingTickets : minTickets)
            .div(Math.pow(10, selectedSale.config.targetAllocation.targetAssetDecimals.toNumber()))
            .decimalPlaces(6)
            .toString(10)}
        </small>
      </div>
    </div>
  );
};

// HELPERS FUNCTION ===========================================================

function getRemainvingTicketAmount(selectedSale: Sale, scale = true) {
  const remainingTickets = BigNumber(selectedSale.config.targetAllocation.targetTicketAmount).minus(
    selectedSale.saleState.ticketsCount
  );
  if (scale) {
    return remainingTickets.div(Math.pow(10, selectedSale.config.targetAllocation.targetAssetDecimals.toNumber()));
  }
  return remainingTickets;
}

class ErrorToDisplay extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ErrorToDisplay";
  }
}
